import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { db } from "./firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { compareCreatedAtDesc, normalizeMapLocation } from "./dataAdapters";
import { normalizeExternalUrl } from "./urlUtils";
import {
  EMPTY_MAP_SUBMISSION,
  MAP_SERVICE_TYPES,
  submitMapLocation,
  validateMapSubmission,
} from "./mapSubmission";
import { filterMapLocations } from "./mapSearch";
import { lookupPostcode } from "./postcodeLookup";

const DEFAULT_CENTER = { lat: 51.505, lng: -0.09 };
const MAP_COLLECTIONS = ["mapLocations", "locations"];

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function ClickToSetMarker({ onSet }) {
  useMapEvents({
    click(e) {
      onSet(e.latlng);
    },
  });
  return null;
}

function MapViewport({ target, locations }) {
  const map = useMap();

  useEffect(() => {
    if (target) {
      map.flyTo([target.lat, target.lng], Math.max(map.getZoom(), 15), {
        duration: 0.6,
      });
      return;
    }
    if (locations.length === 1) {
      map.flyTo([locations[0].lat, locations[0].lng], 15, { duration: 0.6 });
      return;
    }
    if (locations.length > 1) {
      map.fitBounds(
        L.latLngBounds(locations.map((location) => [location.lat, location.lng])),
        { padding: [36, 36], maxZoom: 15 }
      );
    }
  }, [locations, map, target]);

  return null;
}

function MapFieldError({ id, message }) {
  return message ? <span id={id} className="text-xs text-amber-200">{message}</span> : null;
}

export default function LocalMap() {
  const [search, setSearch] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [category, setCategory] = useState("All");
  const [focusedLocation, setFocusedLocation] = useState(null);

  const [postcode, setPostcode] = useState("");
  const [pin, setPin] = useState(null);
  const [locatingPostcode, setLocatingPostcode] = useState(false);
  const [postcodeLookupError, setPostcodeLookupError] = useState("");
  const [postcodeLookupStatus, setPostcodeLookupStatus] = useState("");

  const [submission, setSubmission] = useState({ ...EMPTY_MAP_SUBMISSION });
  const [submissionErrors, setSubmissionErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [approvedPins, setApprovedPins] = useState([]);
  const [pinsLoading, setPinsLoading] = useState(true);
  const [pinsError, setPinsError] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    setPinsLoading(true);
    setPinsError("");

    const rowsByCollection = new Map();
    const settledCollections = new Set();
    const failedCollections = new Set();

    const publishRows = () => {
      const rows = MAP_COLLECTIONS.flatMap(
        (sourceCollection) => rowsByCollection.get(sourceCollection) || []
      ).sort(compareCreatedAtDesc);
      setApprovedPins(rows);
    };

    const settle = (sourceCollection, error = null) => {
      if (settledCollections.has(sourceCollection)) return;
      settledCollections.add(sourceCollection);
      if (error) failedCollections.add(sourceCollection);

      if (settledCollections.size === MAP_COLLECTIONS.length) {
        setPinsLoading(false);
        if (failedCollections.size === MAP_COLLECTIONS.length) {
          setPinsError("Map locations are temporarily unavailable. Please try again shortly.");
        } else if (failedCollections.size > 0) {
          setPinsError("Some older map locations could not be loaded.");
        }
      }
    };

    const unsubs = MAP_COLLECTIONS.map((sourceCollection) =>
      onSnapshot(
        query(
          collection(db, sourceCollection),
          where("status", "==", "approved")
        ),
        (snap) => {
          rowsByCollection.set(
            sourceCollection,
            snap.docs
              .map((doc) =>
                normalizeMapLocation(sourceCollection, doc.id, doc.data())
              )
              .filter(Boolean)
          );
          publishRows();
          settle(sourceCollection);
        },
        (error) => {
          console.error(`Unable to load ${sourceCollection}`, error);
          rowsByCollection.set(sourceCollection, []);
          publishRows();
          settle(sourceCollection, error);
        }
      )
    );

    return () => unsubs.forEach((unsub) => unsub());
  }, [loadAttempt]);

  const filteredPins = useMemo(() => {
    return filterMapLocations(approvedPins, {
      enabled: hasSearched,
      search,
      category,
    });
  }, [approvedPins, category, hasSearched, search]);

  const runSearch = () => {
    setHasSearched(true);
    setFocusedLocation(null);
  };

  const showAllLocations = () => {
    setSearch("");
    setCategory("All");
    setHasSearched(true);
    setFocusedLocation(null);
  };

  const clearSearch = () => {
    setHasSearched(false);
    setSearch("");
    setCategory("All");
    setFocusedLocation(null);
  };

  const handlePostcodeLookup = async () => {
    setPostcodeLookupError("");
    setPostcodeLookupStatus("");
    setLocatingPostcode(true);
    try {
      const result = await lookupPostcode(postcode);
      setPostcode(result.postcode);
      setPin({ lat: result.lat, lng: result.lng });
      setFocusedLocation(null);
      setSubmissionErrors((current) => {
        const next = { ...current };
        delete next.postcode;
        delete next.pin;
        return next;
      });
      setPostcodeLookupStatus(`Pin placed at the approximate centre of ${result.postcode}. You can adjust it by clicking the map.`);
    } catch (error) {
      setPostcodeLookupError(error.message || "Postcode lookup is temporarily unavailable.");
    } finally {
      setLocatingPostcode(false);
    }
  };

  const updateSubmission = (field, value) => {
    setSubmission((current) => ({
      ...current,
      [field]: value,
      ...(field === "includeDirectoryListing" && !value ? { confirmedAccurate: false } : {}),
    }));
    setSubmissionErrors((current) => {
      const next = { ...current };
      if (field === "includeDirectoryListing" && !value) {
        delete next.confirmedAccurate;
        delete next.contact;
        delete next.description;
        delete next.type;
        return next;
      }
      if (!current[field] && !["website", "contactEmail", "contactPhone", "address", "serviceType"].includes(field)) return current;
      delete next[field];
      if (["website", "contactEmail", "contactPhone", "address"].includes(field)) delete next.contact;
      if (field === "serviceType") delete next.type;
      return next;
    });
    setSubmitError("");
    setSubmitSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const values = { ...submission, postcode };
    const nextErrors = validateMapSubmission(values, pin);
    setSubmissionErrors(nextErrors);
    setSubmitError("");
    setSubmitSuccess("");
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitting(true);

    try {
      await submitMapLocation(values, pin);
      setSubmitSuccess(
        values.includeDirectoryListing
          ? "Thank you. Your map location and directory listing have both been sent for review."
          : "Thank you. Your map location has been sent for review."
      );
      setSubmission({ ...EMPTY_MAP_SUBMISSION });
      setPostcode("");
      setPin(null);
      setPostcodeLookupError("");
      setPostcodeLookupStatus("");
      setSubmissionErrors({});
    } catch (err) {
      console.error("Unable to submit map location", err);
      if (err.validationErrors) setSubmissionErrors(err.validationErrors);
      else setSubmitError("Your submission could not be sent. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold">Smart Local Map</h1>
        <p className="text-white/70 mt-1">
          Search for services, activities, or places near you — or add your own.
        </p>
      </div>

      {/* Search */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
        <form onSubmit={(event) => { event.preventDefault(); runSearch(); }} className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_13rem_auto] lg:items-end">
          <label className="grid gap-1 text-sm" htmlFor="map-search">
            Search locations
            <input
              id="map-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Food bank, youth club, advice or sport"
              className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm w-full"
            />
          </label>
          <label className="grid gap-1 text-sm" htmlFor="map-category-filter">
            Category
            <select
              id="map-category-filter"
              value={category}
              onChange={(event) => {
                setCategory(event.target.value);
                setHasSearched(true);
                setFocusedLocation(null);
              }}
              className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
            >
              <option value="All">All categories</option>
              {MAP_SERVICE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </label>
          <div className="flex flex-wrap gap-2">
            <button type="submit" className="px-4 py-2 rounded-xl bg-[#E63946] hover:opacity-90 transition font-semibold">
              Search
            </button>
            <button type="button" onClick={showAllLocations} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 transition font-semibold">
              Show all
            </button>
            <button type="button" onClick={clearSearch} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 transition font-semibold">
              Clear
            </button>
          </div>
        </form>
        <p className="text-xs text-white/60 mt-2">Search by name or service, or show all approved locations and narrow them by category.</p>
        {pinsLoading && <p className="text-xs text-white/60 mt-2">Loading locations…</p>}
        {pinsError && (
          <div role="alert" className="text-xs text-amber-200 mt-2">
            <p>{pinsError}</p>
            <button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)} className="mt-2 rounded-lg border border-amber-100/30 bg-black/20 px-3 py-2 font-semibold">
              Retry
            </button>
          </div>
        )}
        {!pinsLoading && hasSearched && filteredPins.length === 0 && !pinsError && (
          <p className="text-xs text-white/60 mt-2">No matching locations found.</p>
        )}
      </div>

      {/* Map */}
      <div id="community-map" className="scroll-mt-28 rounded-2xl overflow-hidden border border-white/10" aria-label="Community locations map">
        <MapContainer center={[DEFAULT_CENTER.lat, DEFAULT_CENTER.lng]} zoom={13} style={{ height: 420, width: "100%" }}>
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapViewport target={focusedLocation || pin} locations={filteredPins} />
          <ClickToSetMarker onSet={(latlng) => {
            setPin({ lat: latlng.lat, lng: latlng.lng });
            setFocusedLocation(null);
            setPostcodeLookupError("");
            setPostcodeLookupStatus("Pin adjusted manually.");
            setSubmissionErrors((current) => {
              if (!current.pin) return current;
              const next = { ...current };
              delete next.pin;
              return next;
            });
          }} />

          {/* User pin */}
          {pin && (
            <Marker position={[pin.lat, pin.lng]}>
              <Popup>
                <div className="text-sm">
                  <div className="font-semibold">Your pin</div>
                  <div>
                    {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}
                  </div>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Approved + filtered pins */}
          {filteredPins.map((p) => (
            <Marker key={p.id} position={[p.lat, p.lng]}>
              <Popup>
                <div className="text-sm">
                  <div className="font-semibold">{p.name}</div>
                  <div className="opacity-80">{p.serviceType}</div>
                  {p.description && <div className="mt-1">{p.description}</div>}
                  {p.website && (
                    <div className="mt-2">
                      <a href={normalizeExternalUrl(p.website)} target="_blank" rel="noreferrer" className="underline">
                        Website
                      </a>
                    </div>
                  )}
                  {p.contact && <div className="mt-1">{p.contact}</div>}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {hasSearched && !pinsLoading && (
        <section aria-labelledby="map-results-heading" className="rounded-2xl bg-white/5 border border-white/10 p-4">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h2 id="map-results-heading" className="font-semibold text-lg">Location results</h2>
              <p className="text-sm text-white/70" role="status">
                {filteredPins.length === 1 ? "1 approved location" : `${filteredPins.length} approved locations`}
              </p>
            </div>
            {category !== "All" && <p className="text-xs text-white/60">Filtered by {category}</p>}
          </div>

          {filteredPins.length > 0 && (
            <ul className="mt-4 grid gap-3 md:grid-cols-2">
              {filteredPins.map((location) => (
                <li key={`result-${location.id}`} className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <article className="grid h-full gap-2">
                    <div>
                      <h3 className="font-semibold">{location.name}</h3>
                      <p className="text-xs text-white/60">{location.serviceType}{location.postcode ? ` · ${location.postcode}` : ""}</p>
                    </div>
                    {location.description && <p className="text-sm text-white/80">{location.description}</p>}
                    {location.address && <p className="text-xs text-white/70">{location.address}</p>}
                    {location.contact && <p className="text-xs text-white/70">{location.contact}</p>}
                    <div className="mt-auto flex flex-wrap gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setFocusedLocation(location);
                          document.getElementById("community-map")?.scrollIntoView({ behavior: "smooth", block: "center" });
                        }}
                        className="rounded-lg bg-[#E63946] px-3 py-2 text-xs font-semibold text-white hover:opacity-90"
                      >
                        Show on map
                      </button>
                      {location.website && (
                        <a href={normalizeExternalUrl(location.website)} target="_blank" rel="noreferrer" className="rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-xs font-semibold hover:bg-white/15">
                          Visit website
                        </a>
                      )}
                    </div>
                  </article>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Submission form */}
      <div id="add-location" className="scroll-mt-28 rounded-2xl bg-white/5 border border-white/10 p-4">
        <h2 className="font-semibold text-lg">Add a location</h2>
        <p className="text-white/70 text-sm mt-1">
          Place the pin by clicking the map. You can also send the same information to the community directory without completing another form.
        </p>

        {Object.keys(submissionErrors).length > 0 && (
          <div role="alert" className="mt-3 rounded-xl border border-amber-300/30 bg-amber-300/10 p-3 text-sm text-amber-100">
            Please check the highlighted location information.
          </div>
        )}
        {submitError && <div role="alert" className="mt-3 rounded-xl border border-amber-300/30 bg-amber-300/10 p-3 text-sm text-amber-100">{submitError}</div>}
        {submitSuccess && <div role="status" className="mt-3 rounded-xl border border-emerald-300/30 bg-emerald-300/10 p-3 text-sm text-emerald-100">{submitSuccess}</div>}

        <form onSubmit={handleSubmit} noValidate className="grid gap-3 mt-4 md:grid-cols-2">
          <label className="grid gap-1 text-sm" htmlFor="map-location-name">
            Location, service or organisation name <span aria-hidden="true">*</span>
            <input
              id="map-location-name"
              value={submission.name}
              onChange={(event) => updateSubmission("name", event.target.value)}
              maxLength={120}
              required
              aria-invalid={Boolean(submissionErrors.name)}
              aria-describedby={submissionErrors.name ? "map-name-error" : undefined}
              className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
            />
            <MapFieldError id="map-name-error" message={submissionErrors.name} />
          </label>

          <label className="grid gap-1 text-sm" htmlFor="map-service-type">
            Category <span aria-hidden="true">*</span>
            <select
              id="map-service-type"
              value={submission.serviceType}
              onChange={(event) => updateSubmission("serviceType", event.target.value)}
              aria-invalid={Boolean(submissionErrors.serviceType || submissionErrors.type)}
              aria-describedby={(submissionErrors.serviceType || submissionErrors.type) ? "map-type-error" : undefined}
              className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
            >
              {MAP_SERVICE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
            <MapFieldError id="map-type-error" message={submissionErrors.serviceType || submissionErrors.type} />
          </label>

          <div className="grid gap-1 text-sm md:col-span-2">
            <label htmlFor="map-postcode">Postcode</label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                id="map-postcode"
                value={postcode}
                onChange={(event) => {
                  setPostcode(event.target.value);
                  setPostcodeLookupError("");
                  setPostcodeLookupStatus("");
                  setSubmissionErrors((current) => {
                    if (!current.postcode) return current;
                    const next = { ...current };
                    delete next.postcode;
                    return next;
                  });
                }}
                maxLength={12}
                autoComplete="postal-code"
                aria-invalid={Boolean(submissionErrors.postcode || postcodeLookupError)}
                aria-describedby={[
                  "map-postcode-help",
                  submissionErrors.postcode ? "map-postcode-error" : "",
                  postcodeLookupError ? "map-postcode-lookup-error" : "",
                  postcodeLookupStatus ? "map-postcode-lookup-status" : "",
                ].filter(Boolean).join(" ")}
                className="min-w-0 flex-1 bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={handlePostcodeLookup}
                disabled={locatingPostcode || !postcode.trim()}
                className="rounded-xl border border-white/15 bg-white/10 px-4 py-2 font-semibold hover:bg-white/15 disabled:opacity-50"
              >
                {locatingPostcode ? "Finding postcode…" : "Place pin from postcode"}
              </button>
            </div>
            <span id="map-postcode-help" className="text-xs text-white/60">Places the pin at the approximate postcode centre. You can adjust it by clicking the map.</span>
            <MapFieldError id="map-postcode-error" message={submissionErrors.postcode} />
            {postcodeLookupError && <span id="map-postcode-lookup-error" role="alert" className="text-xs text-amber-200">{postcodeLookupError}</span>}
            {postcodeLookupStatus && <span id="map-postcode-lookup-status" role="status" className="text-xs text-emerald-200">{postcodeLookupStatus}</span>}
          </div>

          <label className="grid gap-1 text-sm" htmlFor="map-website">
            Public website
            <input
              id="map-website"
              type="url"
              value={submission.website}
              onChange={(event) => updateSubmission("website", event.target.value)}
              maxLength={300}
              placeholder="https://example.org"
              aria-invalid={Boolean(submissionErrors.website || submissionErrors.contact)}
              aria-describedby={submissionErrors.website ? "map-website-error" : submissionErrors.contact ? "map-contact-error" : undefined}
              className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
            />
            <MapFieldError id="map-website-error" message={submissionErrors.website} />
          </label>

          <label className="grid gap-1 text-sm" htmlFor="map-email">
            Public email
            <input
              id="map-email"
              type="email"
              value={submission.contactEmail}
              onChange={(event) => updateSubmission("contactEmail", event.target.value)}
              maxLength={254}
              aria-invalid={Boolean(submissionErrors.contactEmail || submissionErrors.contact)}
              aria-describedby={submissionErrors.contactEmail ? "map-email-error" : submissionErrors.contact ? "map-contact-error" : undefined}
              className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
            />
            <MapFieldError id="map-email-error" message={submissionErrors.contactEmail} />
          </label>

          <label className="grid gap-1 text-sm" htmlFor="map-phone">
            Public phone
            <input
              id="map-phone"
              type="tel"
              value={submission.contactPhone}
              onChange={(event) => updateSubmission("contactPhone", event.target.value)}
              maxLength={40}
              aria-invalid={Boolean(submissionErrors.contactPhone || submissionErrors.contact)}
              aria-describedby={submissionErrors.contactPhone ? "map-phone-error" : submissionErrors.contact ? "map-contact-error" : undefined}
              className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
            />
            <MapFieldError id="map-phone-error" message={submissionErrors.contactPhone} />
          </label>

          <label className="grid gap-1 text-sm" htmlFor="map-address">
            Public address
            <input
              id="map-address"
              value={submission.address}
              onChange={(event) => updateSubmission("address", event.target.value)}
              maxLength={300}
              aria-invalid={Boolean(submissionErrors.address || submissionErrors.contact)}
              aria-describedby={submissionErrors.address ? "map-address-error" : submissionErrors.contact ? "map-contact-error" : undefined}
              className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
            />
            <MapFieldError id="map-address-error" message={submissionErrors.address} />
          </label>

          <label className="grid gap-1 text-sm md:col-span-2" htmlFor="map-description">
            Description {submission.includeDirectoryListing && <span aria-hidden="true">*</span>}
            <textarea
              id="map-description"
              value={submission.description}
              onChange={(event) => updateSubmission("description", event.target.value)}
              maxLength={1000}
              required={submission.includeDirectoryListing}
              aria-invalid={Boolean(submissionErrors.description)}
              aria-describedby={submissionErrors.description ? "map-description-error" : "map-description-help"}
              className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm min-h-[110px]"
            />
            <span id="map-description-help" className="text-xs text-white/60">
              {submission.includeDirectoryListing
                ? "For the directory, add at least 20 characters explaining what is offered and who it is for."
                : "Optional for a map-only location."}
            </span>
            <MapFieldError id="map-description-error" message={submissionErrors.description} />
          </label>

          <div className="md:col-span-2 grid gap-1">
            <MapFieldError id="map-contact-error" message={submissionErrors.contact} />
            <MapFieldError id="map-pin-error" message={submissionErrors.pin} />
          </div>

          <label className="md:col-span-2 flex items-start gap-3 rounded-xl border border-white/15 bg-black/20 p-4 text-sm" htmlFor="map-include-directory">
            <input
              id="map-include-directory"
              type="checkbox"
              checked={submission.includeDirectoryListing}
              onChange={(event) => updateSubmission("includeDirectoryListing", event.target.checked)}
              className="mt-1"
            />
            <span>
              <span className="block font-semibold">Also submit this as a directory listing</span>
              <span className="mt-1 block text-xs text-white/60">We will reuse the details above. The map location and directory listing will each stay hidden until an administrator approves them.</span>
            </span>
          </label>

          {submission.includeDirectoryListing && (
            <label className="md:col-span-2 flex items-start gap-3 rounded-xl border border-white/10 bg-black/20 p-4 text-sm" htmlFor="map-directory-confirmed">
              <input
                id="map-directory-confirmed"
                type="checkbox"
                checked={submission.confirmedAccurate}
                onChange={(event) => updateSubmission("confirmedAccurate", event.target.checked)}
                aria-invalid={Boolean(submissionErrors.confirmedAccurate)}
                aria-describedby={submissionErrors.confirmedAccurate ? "map-confirmed-error" : "map-public-note"}
                className="mt-1"
              />
              <span>
                I confirm that this information is accurate and that I have permission to submit it for publication.
                <span id="map-public-note" className="mt-1 block text-xs text-white/60">Only enter contact details that may be displayed publicly.</span>
                <MapFieldError id="map-confirmed-error" message={submissionErrors.confirmedAccurate} />
              </span>
            </label>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="md:col-span-2 px-4 py-3 rounded-xl bg-[#E63946] hover:opacity-90 disabled:opacity-60 transition font-semibold"
          >
            {submitting
              ? "Submitting…"
              : submission.includeDirectoryListing
                ? "Submit map location and directory listing"
                : "Submit location for approval"}
          </button>
        </form>
      </div>
    </div>
  );
}
